import { useState } from "react"
import { Button, Divider, Flex, Input, Space, Spin } from "antd"
import { LoadingOutlined } from "@ant-design/icons"
import { useSelector } from "react-redux"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Modal } from "ui/Modal"
import CustomDrawer from "ui/Drawer"
import { Text, Title } from "ui/Typography"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import { colors, spacings } from "themes/variables"
import { numberToCurrency } from "helpers/string"

const VIEWS = {
  DEFAULT: "default",
  CHARGE: "charge",
  REMOVE: "remove"
}

const ManageHoldContent = ({ hold, onClose, onCharge, onVoid }) => {
  const { t } = useTranslation()
  const [view, setView] = useState(VIEWS.DEFAULT)
  const [chargeAmount, setChargeAmount] = useState("")
  const [chargeAmountError, setChargeAmountError] = useState("")
  const [reason, setReason] = useState("")

  const isLoading = useSelector((state) => state.bookings.cardHold.loading)

  const { amount, card_brand, last4, expiration_message, can_capture, can_release } = hold
  const cardLabel = [card_brand, last4 ? `**** ${last4}` : null].filter(Boolean).join(" ")

  const handleChargeAmountChange = (e) => {
    const val = e.target.value
    setChargeAmount(val)
    if (!val || Number(val) <= 0) {
      setChargeAmountError(t("client.payment.card_hold.manage_hold_modal.charge_view.invalid_amount_error"))
    } else if (Number(val) > amount) {
      setChargeAmountError(t("client.payment.card_hold.manage_hold_modal.charge_view.exceed_amount_error", { amount: numberToCurrency(amount) }))
    } else {
      setChargeAmountError("")
    }
  }

  const handleConfirmCharge = () => {
    if (!chargeAmount || Number(chargeAmount) <= 0) {
      setChargeAmountError(t("client.payment.card_hold.manage_hold_modal.charge_view.invalid_amount_error"))
      return
    }
    if (Number(chargeAmount) > amount) {
      setChargeAmountError(t("client.payment.card_hold.manage_hold_modal.charge_view.exceed_amount_error", { amount: numberToCurrency(amount) }))
      return
    }
    onCharge(hold.id, { amount: Number(chargeAmount), reason })
  }

  const handleConfirmVoid = () => {
    onVoid(hold.id)
  }

  const HoldSummary = () => (
    <Flex vertical gap={spacings.XXS} style={{ background: colors.surface1, borderRadius: 8 }} className="py-xs px-s">
      <Flex justify="space-between">
        <Text color={colors.textSecondaryDark}>{t("client.payment.card_hold.manage_hold_modal.hold_amount")}</Text>
        <Text fw={500}>{numberToCurrency(amount)}</Text>
      </Flex>
      {cardLabel && (
        <Flex justify="space-between">
          <Text color={colors.textSecondaryDark}>{t("client.payment.card_hold.manage_hold_modal.card")}</Text>
          <Text>{cardLabel}</Text>
        </Flex>
      )}
      {expiration_message && (
        <Flex justify="space-between">
          <Text color={colors.textSecondaryDark}>{t("client.payment.card_hold.manage_hold_modal.expiration")}</Text>
          <Text color={colors.warning}>{expiration_message}</Text>
        </Flex>
      )}
    </Flex>
  )

  if (view === VIEWS.CHARGE) {
    return (
      <Spin spinning={isLoading} indicator={<LoadingOutlined spin />} size="large">
        <Space direction="vertical" size={24} className="w-full">
          <HoldSummary />

          <Flex vertical gap={spacings.XXS}>
            <Text>
              {t("client.payment.card_hold.manage_hold_modal.charge_view.label")}{" "}
              <Text color={colors.textSecondaryDark}>{t("client.payment.card_hold.manage_hold_modal.charge_view.max_hint", { amount: numberToCurrency(amount) })}</Text>
            </Text>
            <Input
              prefix="$"
              type="number"
              min="0.01"
              step="0.01"
              max={amount}
              value={chargeAmount}
              onChange={handleChargeAmountChange}
              status={chargeAmountError ? "error" : ""}
              placeholder={t("client.payment.card_hold.manage_hold_modal.charge_view.placeholder")}
            />
            {chargeAmountError && <Text color={colors.error}>{chargeAmountError}</Text>}
          </Flex>

          <Flex vertical gap={spacings.XXS}>
            <Text>
              {t("client.payment.card_hold.manage_hold_modal.charge_view.reason_label")}{" "}
              <Text color={colors.textSecondaryDark}>{t("client.payment.card_hold.manage_hold_modal.charge_view.reason_optional")}</Text>
            </Text>
            <Input.TextArea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("client.payment.card_hold.manage_hold_modal.charge_view.reason_placeholder")}
              rows={3}
            />
          </Flex>

          <Flex gap={spacings.S}>
            <Button className="py-l px-xl" color="primary" variant="outlined" disabled={isLoading} onClick={() => setView(VIEWS.DEFAULT)}>
              {t("client.payment.card_hold.manage_hold_modal.charge_view.back")}
            </Button>
            <Button
              type="primary"
              className="py-l px-xl"
              disabled={!chargeAmount || !!chargeAmountError || isLoading}
              loading={isLoading}
              onClick={handleConfirmCharge}
            >
              {t("client.payment.card_hold.manage_hold_modal.charge_view.confirm")}
            </Button>
          </Flex>
        </Space>
      </Spin>
    )
  }

  if (view === VIEWS.REMOVE) {
    return (
      <Spin spinning={isLoading} indicator={<LoadingOutlined spin />} size="large">
        <Space direction="vertical" size={24} className="w-full">
          <HoldSummary />
          <Divider size="small" className="m-0" />
          <Flex vertical gap={spacings.XXS}>
            <Title level={5}>{t("client.payment.card_hold.manage_hold_modal.remove_view.title")}</Title>
            <Text color={colors.textSecondaryDark}>
              {t("client.payment.card_hold.manage_hold_modal.remove_view.description", { amount: numberToCurrency(amount), card: cardLabel || "this card" })}
            </Text>
          </Flex>
          <Flex gap={spacings.S}>
            <Button className="py-l px-xl" color="primary" variant="outlined" disabled={isLoading} onClick={() => setView(VIEWS.DEFAULT)}>
              {t("client.payment.card_hold.manage_hold_modal.remove_view.back")}
            </Button>
            <Button
              danger
              type="primary"
              className="py-l px-xl"
              loading={isLoading}
              disabled={isLoading}
              onClick={handleConfirmVoid}
            >
              {t("client.payment.card_hold.manage_hold_modal.remove_view.confirm")}
            </Button>
          </Flex>
        </Space>
      </Spin>
    )
  }

  return (
    <Space direction="vertical" size={24} className="w-full">
      <HoldSummary />
      <Divider size="small" className="m-0" />
      <Flex gap={spacings.S}>
        {can_capture && (
          <Button type="primary" className="py-l px-xl" onClick={() => setView(VIEWS.CHARGE)}>
            {t("client.payment.card_hold.manage_hold_modal.charge_card")}
          </Button>
        )}
        {can_release && (
          <Button danger className="py-l px-xl" onClick={() => setView(VIEWS.REMOVE)}>
            {t("client.payment.card_hold.manage_hold_modal.remove_hold")}
          </Button>
        )}
        <Button className="py-l px-xl" color="primary" variant="outlined" onClick={onClose}>
          {t("client.payment.card_hold.manage_hold_modal.cancel")}
        </Button>
      </Flex>
    </Space>
  )
}

const ManageHoldModal = ({ open, hold, onClose, onCharge, onVoid }) => {
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm", "md"].includes(breakpoint)

  const { t } = useTranslation()
  const content = <ManageHoldContent hold={hold} onClose={onClose} onCharge={onCharge} onVoid={onVoid} />

  return isMobile ? (
    <CustomDrawer open={open} setOpen={onClose} title={t("client.payment.card_hold.manage_hold_modal.title")}>
      {content}
    </CustomDrawer>
  ) : (
    <Modal centered title={t("client.payment.card_hold.manage_hold_modal.title")} open={open} onCancel={onClose} width={600} maskClosable={false}>
      {content}
    </Modal>
  )
}

ManageHoldContent.propTypes = {
  hold: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    amount: PropTypes.number,
    card_brand: PropTypes.string,
    last4: PropTypes.string,
    expiration_message: PropTypes.string,
    can_capture: PropTypes.bool,
    can_release: PropTypes.bool
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onCharge: PropTypes.func.isRequired,
  onVoid: PropTypes.func.isRequired
}

ManageHoldModal.propTypes = {
  open: PropTypes.bool.isRequired,
  hold: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    amount: PropTypes.number,
    card_brand: PropTypes.string,
    last4: PropTypes.string,
    expiration_message: PropTypes.string,
    can_capture: PropTypes.bool,
    can_release: PropTypes.bool
  }),
  onClose: PropTypes.func.isRequired,
  onCharge: PropTypes.func.isRequired,
  onVoid: PropTypes.func.isRequired
}

export default ManageHoldModal
